import { plural, t } from '@lingui/core/macro';
import { NoddiIcon } from 'noddi-ui-common';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'noddi-ui-web';
import { formatCurrencyAmount, type IncludedOrderLine, sumIncludedOrderLines } from 'noddi-util';
import { PriceListDiscountLabel } from 'noddi-web';
import { useState } from 'react';
import { getCommonTranslations } from '../../../utils/commonTranslations';

type OpenOrdersProductsProps = {
  /** Fixed inclusions for the car: non-editable services and open-order lines. */
  lines: IncludedOrderLine[];
  /** Catalog deal already named on the agreement card — omit the matching name on a line. */
  contextPriceListName?: string | null;
  /**
   * Drop the box when the lines add nothing to pay. Only for surfaces that list the
   * same lines elsewhere (the proposal card's order summary does) — on the booking
   * flow's Services step this is the only place a fixed service is named, and a free
   * one is still a service the customer is about to be booked for.
   */
  hideWhenFree?: boolean;
};

const ACCORDION_VALUE = 'included-items';

/**
 * Fixed inclusions: open-order residual lines and non-editable primaries.
 *
 * Collapsed to a count and a sum because none of it is a choice the customer makes —
 * as priced cards they competed with the services the booking is actually about.
 */
export const OpenOrdersProducts = ({ lines, contextPriceListName, hideWhenFree }: OpenOrdersProductsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const total = sumIncludedOrderLines(lines);
  if (!total) {
    return null;
  }
  // Nothing to add at checkout means nothing to warn about: at zero the box read as
  // an extra charge the customer had to work out, for money that was never there.
  if (hideWhenFree && total.amount === 0) {
    return null;
  }

  const priceListDiscountLabel = getCommonTranslations().priceListDiscountLabel;

  return (
    <Accordion
      value={isExpanded ? [ACCORDION_VALUE] : []}
      onValueChange={(value) => setIsExpanded(value.includes(ACCORDION_VALUE))}
      className='rounded-xl border border-border bg-muted'
    >
      <AccordionItem value={ACCORDION_VALUE} className='border-b-0'>
        <AccordionTrigger headingLevel={2} className='gap-5 px-5 py-4 hover:no-underline'>
          <span className='flex flex-1 items-start gap-4'>
            <NoddiIcon name='Info' size='small' className='mt-0.5 shrink-0 text-muted-foreground' />
            <span className='flex flex-col gap-1'>
              <span>
                {plural(lines.length, {
                  one: '# item from an existing order is added at checkout',
                  other: '# items from an existing order are added at checkout'
                })}
              </span>
              <span className='font-normal text-foreground'>
                {t`These items are already ordered for this car but not paid. They cannot be changed here, and are paid together with this booking.`}
              </span>
            </span>
          </span>
          <span className='whitespace-nowrap'>{formatCurrencyAmount({ money: total, decimals: 0 })}</span>
        </AccordionTrigger>

        <AccordionContent className='px-5 pb-4'>
          <ul className='flex flex-col pl-9'>
            {lines.map((line) => (
              <li
                key={line.key}
                className='flex justify-between gap-5 border-t border-border py-2 text-muted-foreground'
              >
                <span>
                  {line.quantity != null && line.quantity !== 1 ? `${line.quantity}x ${line.name}` : line.name}
                  <PriceListDiscountLabel
                    priceListName={line.priceListName}
                    discountPercent={line.discountPercent}
                    contextPriceListName={contextPriceListName}
                    translations={priceListDiscountLabel}
                  />
                </span>
                <span className='whitespace-nowrap'>
                  {formatCurrencyAmount({ money: line.grossAmount, decimals: 0 })}
                </span>
              </li>
            ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
